import { useCallback, useEffect, useRef, useState } from "react";
import { ProduceStream } from "types";
import { MediaFilesController } from "lib/MediaFileController";
import { liveChatUserName, recordingParticipantId } from "lib/liveStreamHelpers";
import {
  createRecordingUploader,
  RecordingUploader,
} from "lib/recordingSegmentUploader";
import type {
  RecordingResult,
  RecordingMetadata,
  UploadProgress,
  UploadPhase,
} from "lib/recordingSegmentUploader";

// Re-exported so existing consumers can keep importing these from the hook.
export type { RecordingResult, RecordingMetadata, UploadProgress, UploadPhase };

const MIN_RECORDING_DURATION_MS = 1_000;
// Client-side hard cap on recording length. Kept below the server's Busboy
// `fileSize` limit (2 GB): at the 2.5 Mbps video bitrate, 2 GB ≈ ~110 min, so
// 90 min leaves headroom for the per-user audio tracks that share the upload.
// Consumers arm a timer on recordingStartedAt and auto-stop+upload on expiry
// (a graceful stop — the recording is preserved, not lost). If you raise this,
// raise the Busboy limit in graphql-server-dev/recordingUpload.js too.
export const MAX_RECORDING_DURATION_MS = 90 * 60 * 1000;
const DRAW_INTERVAL_MS = 42; // ~24fps
// Concurrent per-user audio recorders. These are cheap (32-64 kbps audio-only
// encoders), so capable hosts get a higher ceiling; the GC below makes the cap
// a rolling window of *active* speakers rather than lifetime-distinct ones.
const MAX_PER_USER_RECORDERS =
  (typeof navigator !== "undefined" && (navigator.hardwareConcurrency || 4) >= 4) ? 24 : 16;
// A departed participant's recorder is reclaimed only after it's been detached
// (silent) for this long, so brief drop/reconnect blips never lose a slot.
const PER_USER_SLOT_GC_GRACE_MS = 5 * 60 * 1000;
const PER_USER_AUDIO_BITRATE_DEFAULT = 64_000;
const PER_USER_AUDIO_BITRATE_LOW = 32_000;

function sanitizeUserId(raw: string): string {
  return String(raw).replace(/[^a-zA-Z0-9_-]/g, "");
}

/**
 * Audio tracks coming from getDisplayMedia({audio:true}) carry the same
 * `kindType: "screen"` marker as the screen-video tracks (set in
 * useProduceController.onScreenShare). They must be excluded when picking
 * the speaker's microphone — otherwise per-user transcription captures
 * whatever happens to play on the shared tab (usually silence).
 */
function getMicAudioTracks(stream: MediaStream): MediaStreamTrack[] {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return stream.getAudioTracks().filter((t) => (t as any).kindType !== "screen");
}

const WIDTH = 1280;
const HEIGHT = 720;
const THUMB_H = 160;
const THUMB_W = Math.round(THUMB_H * (4 / 3)); // ~213px
const THUMB_PAD = 8;
const THUMB_RADIUS = 8;

/** Draw a single video into a rectangular area, maintaining aspect ratio */
function drawVideo(
  ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
  video: HTMLVideoElement,
  x: number, y: number, w: number, h: number,
) {
  const vw = video.videoWidth || w;
  const vh = video.videoHeight || h;
  const scale = Math.min(w / vw, h / vh);
  const dw = vw * scale;
  const dh = vh * scale;
  ctx.drawImage(video, x + (w - dw) / 2, y + (h - dh) / 2, dw, dh);
}

const AVATAR_BG = "#2a2a3e";

function pickInitialColor(seed: string): string {
  // Stable per-seed (userId) so the same person gets the same colored
  // background across calls. Eight tasteful saturated values; the goal is
  // to give voice-only callers a visually-distinguishable backdrop, not a
  // perfect palette.
  const palette = ["#5b6ee1", "#8b5fbf", "#1f9d8a", "#c97c4a", "#c14e6f", "#3478b8", "#7b8a3a", "#9b5a3c"];
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) | 0;
  return palette[Math.abs(h) % palette.length];
}

function getInitials(name: string): string {
  const trimmed = (name || "").trim();
  if (!trimmed) return "?";
  const parts = trimmed.split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 1).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/**
 * Draw the avatar (or initials fallback) for a voice-only participant into
 * the same rectangle a video would have used. Same call shape as
 * drawVideo — drop-in replacement when the tile has no live video track.
 */
function drawAvatar(
  ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
  stream: ProduceStream,
  avatarImage: HTMLImageElement | null,
  x: number, y: number, w: number, h: number,
) {
  // Filled tile background — distinct from the global canvas background
  // (#1a1a2e) so multiple voice-only tiles in a grid stay visually separated.
  const userId = String(stream.user?.id ?? stream.id ?? "");
  const name = stream.user ? liveChatUserName(stream.user) : "";
  ctx.fillStyle = userId ? pickInitialColor(userId) : AVATAR_BG;
  ctx.fillRect(x, y, w, h);

  const cx = x + w / 2;
  const cy = y + h / 2;
  const radius = Math.min(w, h) * 0.22;

  // Avatar image fits inside the circle; HTMLImageElement.complete +
  // naturalWidth guards against drawing a half-loaded image.
  if (avatarImage && avatarImage.complete && avatarImage.naturalWidth > 0) {
    ctx.save();
    ctx.beginPath();
    ctx.arc(cx, cy, radius, 0, Math.PI * 2);
    ctx.closePath();
    ctx.clip();
    ctx.drawImage(avatarImage, cx - radius, cy - radius, radius * 2, radius * 2);
    ctx.restore();
  } else {
    ctx.fillStyle = "rgba(0,0,0,0.25)";
    ctx.beginPath();
    ctx.arc(cx, cy, radius, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = "#ffffff";
    const fontPx = Math.round(radius * 0.9);
    ctx.font = `bold ${fontPx}px sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(getInitials(name), cx, cy);
    ctx.textAlign = "start";
    ctx.textBaseline = "alphabetic";
  }

  // Screen name below the avatar.
  if (name) {
    const labelFont = Math.max(12, Math.min(20, Math.round(h * 0.06)));
    ctx.fillStyle = "rgba(255,255,255,0.85)";
    ctx.font = `${labelFont}px sans-serif`;
    ctx.textAlign = "center";
    ctx.fillText(name, cx, cy + radius + labelFont * 1.4);
    ctx.textAlign = "start";
  }
}

/**
 * Preload avatar images for every stream that may end up voice-only. The
 * <img> sits in memory until cleanup; canvas.drawImage reads from it.
 * Same-origin URLs from this app's `/graphql/image-thumbnails` proxy don't
 * taint the canvas, so MediaRecorder can still capture the composited
 * frame. If a URL fails to load or is cross-origin tainted, drawAvatar
 * falls back to initials.
 */
function loadAvatarImage(stream: ProduceStream): HTMLImageElement | null {
  const urlPart = stream.user?.user?.avatar?.urlPart;
  if (!urlPart) return null;
  let src: string;
  try {
    src = MediaFilesController.getImageThumbnail(urlPart, "240p");
  } catch {
    return null;
  }
  if (!src) return null;
  const img = new Image();
  img.crossOrigin = "anonymous";
  img.src = src;
  return img;
}

/** Draw rounded rectangle clip */
function roundedRect(ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.arcTo(x + w, y, x + w, y + r, r);
  ctx.lineTo(x + w, y + h - r);
  ctx.arcTo(x + w, y + h, x + w - r, y + h, r);
  ctx.lineTo(x + r, y + h);
  ctx.arcTo(x, y + h, x, y + h - r, r);
  ctx.lineTo(x, y + r);
  ctx.arcTo(x, y, x + r, y, r);
  ctx.closePath();
}

interface CompositorState {
  selectedStreamId: string | null;
  meId: string | null;
  streams: ProduceStream[];
}

/** Common draw logic for both advanced (OffscreenCanvas) and fallback (Canvas) paths */
function drawCompositeFrame(
  ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
  videoElements: Map<string, HTMLVideoElement>,
  avatarImages: Map<string, HTMLImageElement>,
  state: CompositorState,
) {
  const { selectedStreamId, streams } = state;

  // Per stream, pick a tile renderer: live video frame if the <video> has
  // delivered at least one frame (readyState >= 2), otherwise avatar.
  // Voice-only calls and remote-camera-off both go through the avatar path.
  type TileSource =
    | { kind: "video"; video: HTMLVideoElement }
    | { kind: "avatar"; image: HTMLImageElement | null };
  const tiles: { stream: ProduceStream; source: TileSource }[] = [];
  for (const stream of streams) {
    const video = videoElements.get(stream.id);
    if (video && video.readyState >= 2 && stream.hasVideo !== false) {
      tiles.push({ stream, source: { kind: "video", video } });
    } else {
      tiles.push({ stream, source: { kind: "avatar", image: avatarImages.get(stream.id) || null } });
    }
  }

  ctx.fillStyle = "#1a1a2e";
  ctx.fillRect(0, 0, WIDTH, HEIGHT);

  const drawTile = (
    tile: { stream: ProduceStream; source: TileSource },
    x: number, y: number, w: number, h: number,
  ) => {
    if (tile.source.kind === "video") {
      drawVideo(ctx, tile.source.video, x, y, w, h);
    } else {
      drawAvatar(ctx, tile.stream, tile.source.image, x, y, w, h);
    }
  };

  if (tiles.length === 0) {
    // No streams at all — just background + REC
  } else if (tiles.length === 1) {
    drawTile(tiles[0], 0, 0, WIDTH, HEIGHT);
  } else if (selectedStreamId && tiles.some((t) => t.stream.id === selectedStreamId)) {
    // Focused layout
    const main = tiles.find((t) => t.stream.id === selectedStreamId)!;
    drawTile(main, 0, 0, WIDTH, HEIGHT);

    const others = tiles.filter((t) => t.stream.id !== selectedStreamId);
    others.forEach((tile, i) => {
      const tx = THUMB_PAD + i * (THUMB_W + THUMB_PAD);
      const ty = HEIGHT - THUMB_H - THUMB_PAD;

      ctx.save();
      roundedRect(ctx, tx, ty, THUMB_W, THUMB_H, THUMB_RADIUS);
      ctx.clip();
      ctx.fillStyle = "#000";
      ctx.fillRect(tx, ty, THUMB_W, THUMB_H);
      drawTile(tile, tx, ty, THUMB_W, THUMB_H);
      ctx.restore();

      ctx.strokeStyle = "rgba(255,255,255,0.3)";
      ctx.lineWidth = 1;
      roundedRect(ctx, tx, ty, THUMB_W, THUMB_H, THUMB_RADIUS);
      ctx.stroke();
    });
  } else {
    // Grid layout
    const count = tiles.length;
    const cols = count <= 1 ? 1 : count <= 4 ? 2 : count <= 9 ? 3 : 4;
    const rows = Math.ceil(count / cols);
    const cellW = WIDTH / cols;
    const cellH = HEIGHT / rows;

    tiles.forEach((tile, i) => {
      const col = i % cols;
      const row = Math.floor(i / cols);
      drawTile(tile, col * cellW, row * cellH, cellW, cellH);
    });
  }

  // REC indicator
  ctx.fillStyle = "#ff4444";
  ctx.beginPath();
  ctx.arc(WIDTH - 30, 30, 10, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = "#ffffff";
  ctx.font = "bold 14px sans-serif";
  ctx.fillText("REC", WIDTH - 62, 35);
}

/** Advanced compositor: uses MediaStreamTrackGenerator (Chrome 94+) */
function createAdvancedCompositor(
  streams: ProduceStream[],
  videoElementsRef: React.MutableRefObject<Map<string, HTMLVideoElement>>,
  avatarImagesRef: React.MutableRefObject<Map<string, HTMLImageElement>>,
  drawIntervalRef: React.MutableRefObject<number>,
  stateRef: React.MutableRefObject<CompositorState>,
) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const G = (globalThis as any).MediaStreamTrackGenerator;
  if (!G) return null;

  const canvas = new OffscreenCanvas(WIDTH, HEIGHT);
  const ctx = canvas.getContext("2d")! as OffscreenCanvasRenderingContext2D;

  const videoElements = new Map<string, HTMLVideoElement>();
  const avatarImages = new Map<string, HTMLImageElement>();
  streams.forEach((s) => {
    if (s.stream) {
      const video = document.createElement("video");
      video.srcObject = s.stream;
      video.muted = true;
      video.playsInline = true;
      video.play().catch(() => {});
      videoElements.set(s.id, video);
    }
    const img = loadAvatarImage(s);
    if (img) avatarImages.set(s.id, img);
  });
  videoElementsRef.current = videoElements;
  avatarImagesRef.current = avatarImages;

  const generator = new G({ kind: "video" });
  const writer = generator.writable.getWriter();
  const videoTrack: MediaStreamTrack = generator;

  let frameCount = 0;
  const drawFrame = () => {
    drawCompositeFrame(ctx, videoElementsRef.current, avatarImagesRef.current, stateRef.current);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const VF = (globalThis as any).VideoFrame;
    if (VF) {
      try {
        const frame = new VF(canvas, { timestamp: performance.now() * 1000 });
        writer.write(frame).then(
          () => frame.close(),
          () => frame.close(),
        );
        frameCount++;
        if (frameCount <= 3) {
          // eslint-disable-next-line no-console
          console.info(`[recording] Frame ${frameCount} pushed, size=${canvas.width}x${canvas.height}`);
        }
      } catch (e) {
        if (frameCount <= 3) {
          // eslint-disable-next-line no-console
          console.error("[recording] VideoFrame creation failed:", e);
        }
      }
    }
  };

  drawFrame();
  drawIntervalRef.current = window.setInterval(drawFrame, DRAW_INTERVAL_MS);

  const cleanup = () => {
    clearInterval(drawIntervalRef.current);
    drawIntervalRef.current = 0;
    writer.close().catch(() => {});
    videoElementsRef.current.forEach((v) => {
      try { v.pause(); v.srcObject = null; } catch { /* */ }
    });
    videoElementsRef.current.clear();
    avatarImagesRef.current.clear();
  };

  return { videoTrack, cleanup };
}

/** Fallback compositor: uses visible Canvas + captureStream (Safari/Firefox) */
function createFallbackCompositor(
  streams: ProduceStream[],
  videoElementsRef: React.MutableRefObject<Map<string, HTMLVideoElement>>,
  avatarImagesRef: React.MutableRefObject<Map<string, HTMLImageElement>>,
  drawIntervalRef: React.MutableRefObject<number>,
  stateRef: React.MutableRefObject<CompositorState>,
) {
  // Create a hidden DOM canvas for captureStream
  const canvas = document.createElement("canvas");
  canvas.width = WIDTH;
  canvas.height = HEIGHT;
  canvas.style.position = "fixed";
  canvas.style.left = "-9999px";
  canvas.style.top = "-9999px";
  canvas.style.pointerEvents = "none";
  canvas.style.opacity = "0";
  document.body.appendChild(canvas);

  const ctx = canvas.getContext("2d")!;

  const videoElements = new Map<string, HTMLVideoElement>();
  const avatarImages = new Map<string, HTMLImageElement>();
  streams.forEach((s) => {
    if (s.stream) {
      const video = document.createElement("video");
      video.srcObject = s.stream;
      video.muted = true;
      video.playsInline = true;
      video.play().catch(() => {});
      videoElements.set(s.id, video);
    }
    const img = loadAvatarImage(s);
    if (img) avatarImages.set(s.id, img);
  });
  videoElementsRef.current = videoElements;
  avatarImagesRef.current = avatarImages;

  const drawFrame = () => {
    drawCompositeFrame(ctx, videoElementsRef.current, avatarImagesRef.current, stateRef.current);
  };

  drawFrame();
  drawIntervalRef.current = window.setInterval(drawFrame, DRAW_INTERVAL_MS);

  // captureStream works in all modern browsers on visible canvas
  const capturedStream = canvas.captureStream(24);
  const videoTrack = capturedStream.getVideoTracks()[0];

  if (!videoTrack) {
    // eslint-disable-next-line no-console
    console.error("[recording] Fallback compositor: captureStream produced no video track");
    document.body.removeChild(canvas);
    return null;
  }

  // eslint-disable-next-line no-console
  console.info("[recording] Using fallback canvas compositor (captureStream)");

  const cleanup = () => {
    clearInterval(drawIntervalRef.current);
    drawIntervalRef.current = 0;
    capturedStream.getTracks().forEach((t) => t.stop());
    videoElementsRef.current.forEach((v) => {
      try { v.pause(); v.srcObject = null; } catch { /* */ }
    });
    videoElementsRef.current.clear();
    avatarImagesRef.current.clear();
    try { document.body.removeChild(canvas); } catch { /* */ }
  };

  return { videoTrack, cleanup };
}

// The MediaStreamTrackGenerator path (createAdvancedCompositor) is an
// OffscreenCanvas perf optimization, but on current Chrome builds the
// VideoFrames written to the generator never reach MediaRecorder's encoder:
// `ondataavailable` never fires, so the recording seals with 0 video bytes
// (no playable file, no poster thumbnail, no audio to transcribe). The
// canvas.captureStream() path feeds MediaRecorder reliably on every browser,
// so we use it unconditionally. Flip this to true only to A/B-test the
// generator path on a Chrome build that's confirmed to record from it.
const USE_TRACK_GENERATOR = false;

/** Create compositor: captureStream (reliable) — generator path gated off. */
function createCanvasCompositor(
  streams: ProduceStream[],
  videoElementsRef: React.MutableRefObject<Map<string, HTMLVideoElement>>,
  avatarImagesRef: React.MutableRefObject<Map<string, HTMLImageElement>>,
  drawIntervalRef: React.MutableRefObject<number>,
  stateRef: React.MutableRefObject<CompositorState>,
) {
  if (USE_TRACK_GENERATOR) {
    // Advanced path (MediaStreamTrackGenerator — Chrome 94+). Disabled: see above.
    const advanced = createAdvancedCompositor(streams, videoElementsRef, avatarImagesRef, drawIntervalRef, stateRef);
    if (advanced) return advanced;
  }

  // Canvas + captureStream — universally reliable input for MediaRecorder.
  return createFallbackCompositor(streams, videoElementsRef, avatarImagesRef, drawIntervalRef, stateRef);
}

interface PerUserRecorderSlot {
  userId: string;
  recorder: MediaRecorder;
  mimeType: string;
  // Stable sink that the MediaRecorder records from. We reconnect source
  // nodes to `dest` when the user's mic track changes (mute/unmute/device
  // switch) so the recording continues uninterrupted — silence during mute,
  // speech when unmuted.
  dest: MediaStreamAudioDestinationNode;
  sourceNode: MediaStreamAudioSourceNode | null;
  currentTrackId: string | null;
  // ms from recording start to this slot's creation; passed to the uploader on
  // every chunk so the server can realign this speaker's transcript onto the
  // global timeline (late joiners).
  offsetMs: number;
  // Set when the participant leaves (source detached → silence); cleared if
  // they rejoin. A slot detached longer than the grace window can be GC'd to
  // free a recorder for a new speaker (its audio is already in the uploader).
  detachedAt?: number;
  finished?: Promise<void>;
}

function pickAudioMimeType(): string {
  const candidates = [
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/mp4;codecs=mp4a.40.2",
    "audio/mp4",
  ];
  for (const c of candidates) {
    try { if (MediaRecorder.isTypeSupported(c)) return c; } catch { /* */ }
  }
  return "audio/webm";
}

// iOS 14.0–14.4 only exposes `webkitAudioContext`; from 14.5 `AudioContext`
// is unprefixed. On iOS the context is also created "suspended" unless it
// happens inside a user gesture — resume() is a safe no-op when already
// running.
function createAudioContext(): AudioContext | null {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const Ctor: typeof AudioContext | undefined =
    (window as any).AudioContext || (window as any).webkitAudioContext;
  if (!Ctor) return null;
  try {
    const ctx = new Ctor();
    if (ctx.state === "suspended") ctx.resume().catch(() => { /* */ });
    return ctx;
  } catch {
    return null;
  }
}

function startPerUserRecorder(
  userId: string,
  audioTrack: MediaStreamTrack,
  bitrate: number,
  ctx: AudioContext,
  offsetMs: number,
  uploader: RecordingUploader,
): PerUserRecorderSlot | null {
  try {
    const dest = ctx.createMediaStreamDestination();
    const sourceNode = ctx.createMediaStreamSource(new MediaStream([audioTrack]));
    sourceNode.connect(dest);
    const mimeType = pickAudioMimeType();
    const recorder = new MediaRecorder(dest.stream, { mimeType, audioBitsPerSecond: bitrate });
    // Each 1s chunk streams to the uploader keyed by userId; the uploader
    // either segments it live or buffers it for the legacy single POST.
    recorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) uploader.pushAudio(userId, mimeType, offsetMs, e.data);
    };
    recorder.onerror = (e) => {
      // eslint-disable-next-line no-console
      console.warn(`[recording] per-user recorder ${userId} error:`, e);
    };
    recorder.start(1000);
    return { userId, recorder, mimeType, dest, sourceNode, currentTrackId: audioTrack.id, offsetMs };
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn(`[recording] Failed to start per-user recorder for ${userId}:`, err);
    return null;
  }
}

/**
 * Swap the audio source feeding this slot's destination. Called whenever the
 * user mutes (track → null), unmutes (null → track), or switches mic devices
 * (old track → new track). The MediaRecorder stays running; silence is
 * captured during mute.
 */
function setPerUserRecorderSource(
  slot: PerUserRecorderSlot,
  newTrack: MediaStreamTrack | null,
  ctx: AudioContext,
): void {
  const newId = newTrack ? newTrack.id : null;
  if (slot.currentTrackId === newId) return;

  if (slot.sourceNode) {
    try { slot.sourceNode.disconnect(); } catch { /* */ }
    slot.sourceNode = null;
  }
  slot.currentTrackId = null;

  if (newTrack) {
    try {
      const src = ctx.createMediaStreamSource(new MediaStream([newTrack]));
      src.connect(slot.dest);
      slot.sourceNode = src;
      slot.currentTrackId = newTrack.id;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn(`[recording] Failed to swap source for ${slot.userId}:`, e);
    }
  }
}

// Stop a per-user recorder, flushing its final chunk into the uploader (via
// requestData → ondataavailable) before resolving. No blob is built here — the
// uploader owns the per-user audio buffers.
function stopPerUserRecorder(slot: PerUserRecorderSlot): Promise<void> {
  if (slot.finished) return slot.finished;
  slot.finished = new Promise((resolve) => {
    const { recorder, userId } = slot;
    const cleanup = () => {
      if (slot.sourceNode) { try { slot.sourceNode.disconnect(); } catch { /* */ } slot.sourceNode = null; }
    };
    if (recorder.state === "inactive") {
      cleanup();
      resolve();
      return;
    }
    recorder.onstop = () => {
      cleanup();
      resolve();
    };
    try { recorder.requestData(); } catch { /* */ }
    try { recorder.stop(); } catch (err) {
      cleanup();
      // eslint-disable-next-line no-console
      console.warn(`[recording] stop per-user recorder ${userId} failed:`, err);
      resolve();
    }
  });
  return slot.finished;
}

export default function useConferenceRecording() {
  const [recording, setRecording] = useState(false);
  const [recordingStartedAt, setRecordingStartedAt] = useState<number | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<UploadProgress | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const uploaderRef = useRef<RecordingUploader | null>(null);
  const drawIntervalRef = useRef<number>(0);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const audioDestRef = useRef<MediaStreamAudioDestinationNode | null>(null);
  const audioSourcesRef = useRef<MediaStreamAudioSourceNode[]>([]);
  // Tracks IDs currently fed into the main-video audio mix. Used by
  // updateStreams to skip rebuilds when only metadata changed (active-
  // speaker isSpeaking flips arrive every 500 ms and re-create the
  // `streams` array reference, but the audio track set is unchanged).
  const mixedAudioTrackIdsRef = useRef<string[]>([]);
  const videoElementsRef = useRef<Map<string, HTMLVideoElement>>(new Map());
  const avatarImagesRef = useRef<Map<string, HTMLImageElement>>(new Map());
  const recordingStartTimeRef = useRef<number>(0);
  const compositorCleanupRef = useRef<(() => void) | null>(null);
  const metadataRef = useRef<RecordingMetadata | undefined>(undefined);
  const perUserRecordersRef = useRef<Map<string, PerUserRecorderSlot>>(new Map());
  const perUserAudioCtxRef = useRef<AudioContext | null>(null);
  // userIds we've already warned about being dropped from per-user
  // transcription (cap reached). Prevents the 500ms updateStreams cadence
  // from spamming the console. Reset on each startRecording.
  const perUserCapWarnedRef = useRef<Set<string>>(new Set());
  const compositorStateRef = useRef<CompositorState>({
    selectedStreamId: null,
    meId: null,
    streams: [],
  });

  const startRecording = useCallback((streams: ProduceStream[], meId?: string, meta?: RecordingMetadata): boolean => {
    if (recorderRef.current || streams.length === 0) return false;

    metadataRef.current = meta;
    compositorStateRef.current = {
      selectedStreamId: null,
      meId: meId ?? null,
      streams,
    };

    // Pick the video container/codec up front so the uploader can open its
    // session with the right extension, and the MediaRecorder reuses it below.
    const videoMimeType = MediaRecorder.isTypeSupported("video/webm;codecs=vp9,opus")
      ? "video/webm;codecs=vp9,opus"
      : MediaRecorder.isTypeSupported("video/webm;codecs=vp8,opus")
        ? "video/webm;codecs=vp8,opus"
        : "video/webm";

    // Create the uploader (mints recordingId, opens a streaming session in the
    // background; falls back to a single POST if the server lacks the segmented
    // endpoints). Per-user recorders below stream their chunks into it.
    const uploader = createRecordingUploader(meta, videoMimeType);
    uploaderRef.current = uploader;

    // Collect all live audio tracks for mixing + start per-user recorders
    // so we can later transcribe each participant independently.
    const liveAudioTracks: MediaStreamTrack[] = [];
    const perUserBitrate =
      (navigator.hardwareConcurrency || 4) < 4
        ? PER_USER_AUDIO_BITRATE_LOW
        : PER_USER_AUDIO_BITRATE_DEFAULT;
    perUserRecordersRef.current = new Map();
    perUserCapWarnedRef.current = new Set();
    if (!perUserAudioCtxRef.current || perUserAudioCtxRef.current.state === "closed") {
      perUserAudioCtxRef.current = createAudioContext();
    }
    const perUserCtx = perUserAudioCtxRef.current;

    streams.forEach((s) => {
      if (!s.stream) return;
      // Screen-share stream is a secondary entry for the same user — its
      // tracks are video-only (or contain "share tab audio" which duplicates
      // the speaker). Skip it for audio collection: mic audio is already in
      // the camera-stream entry for the same userId.
      if (s.isScreen) return;
      // For the local user (me), audio lives in a separate MediaStream
      // (`audioStream`) since the render stream is video-only. Remote
      // consumers deliver audio in `stream` directly.
      const audioSource = s.audioStream || s.stream;
      // Main-video mix: include ALL audio (mic + screen-audio if shared) so
      // the recording playback hears whatever the user wanted shared.
      const allAudioTracks = audioSource.getAudioTracks().filter((t) => t.readyState === "live");
      allAudioTracks.forEach((t) => liveAudioTracks.push(t));
      // Per-user transcription: mic only — drop screen-audio.
      const micTracks = getMicAudioTracks(audioSource).filter((t) => t.readyState === "live");

      // Start per-user recorder (one per participant) for later transcription.
      // Use the account id (resolvable to the current name) over the ephemeral
      // livechat session id, and keep it identical to the participants metadata
      // built in ConferenceView so the backend can join tracks to names.
      const rawUserId = s.user ? recordingParticipantId(s.user) : "";
      const userId = rawUserId ? sanitizeUserId(rawUserId) : "";
      if (
        userId &&
        micTracks.length > 0 &&
        !perUserRecordersRef.current.has(userId) &&
        perUserCtx
      ) {
        if (perUserRecordersRef.current.size < MAX_PER_USER_RECORDERS) {
          // offset 0: these recorders start synchronously with the recording.
          const slot = startPerUserRecorder(userId, micTracks[0], perUserBitrate, perUserCtx, 0, uploader);
          if (slot) perUserRecordersRef.current.set(userId, slot);
        } else if (!perUserCapWarnedRef.current.has(userId)) {
          perUserCapWarnedRef.current.add(userId);
          // eslint-disable-next-line no-console
          console.warn(
            `[recording] per-user transcription cap (${MAX_PER_USER_RECORDERS}) reached — ${userId} will NOT be transcribed`,
          );
        }
      }
    });

    // Try canvas compositing (advanced → fallback)
    const compositor = createCanvasCompositor(streams, videoElementsRef, avatarImagesRef, drawIntervalRef, compositorStateRef);

    const recordingStream = new MediaStream();

    if (compositor) {
      recordingStream.addTrack(compositor.videoTrack);
      compositorCleanupRef.current = compositor.cleanup;
    } else {
      // Last resort: record first live video track only
      for (const s of streams) {
        if (!s.stream) continue;
        for (const t of s.stream.getVideoTracks()) {
          if (t.readyState === "live") {
            recordingStream.addTrack(t);
            break;
          }
        }
        if (recordingStream.getVideoTracks().length > 0) break;
      }
    }

    // Mix audio through a persistent AudioContext so participants who join
    // *after* the recording has started get their voice added to the main
    // video's audio track. We create the context unconditionally (even if
    // the call currently has 0–1 speakers) so updateStreams can swap sources
    // in and out as the lineup changes.
    const audioCtx = createAudioContext();
    if (audioCtx) {
      const audioDest = audioCtx.createMediaStreamDestination();
      audioCtxRef.current = audioCtx;
      audioDestRef.current = audioDest;
      const sources: MediaStreamAudioSourceNode[] = [];
      liveAudioTracks.forEach((track) => {
        try {
          const source = audioCtx.createMediaStreamSource(new MediaStream([track]));
          source.connect(audioDest);
          sources.push(source);
        } catch { /* ignore */ }
      });
      audioSourcesRef.current = sources;
      mixedAudioTrackIdsRef.current = liveAudioTracks.map((t) => t.id);
      audioDest.stream.getAudioTracks().forEach((t) => recordingStream.addTrack(t));
    } else if (liveAudioTracks.length > 0) {
      // AudioContext unavailable (very old browser) — fall back to a single
      // direct track. Late joiners will not be audible on the main video,
      // but per-user transcripts are unaffected.
      recordingStream.addTrack(liveAudioTracks[0]);
    }

    if (recordingStream.getTracks().length === 0) {
      // eslint-disable-next-line no-console
      console.error("[recording] No tracks in recording stream after setup");
      compositor?.cleanup();
      uploader.abort();
      uploaderRef.current = null;
      return false;
    }

    const recorder = new MediaRecorder(recordingStream, {
      mimeType: videoMimeType,
      videoBitsPerSecond: 2_500_000,
    });

    // Stream each 1s chunk to the uploader instead of buffering the whole
    // recording in memory — keeps browser RAM flat over a 60-min call.
    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) uploader.pushVideo(e.data, videoMimeType);
    };
    recorder.onerror = (e) => {
      // eslint-disable-next-line no-console
      console.error("[recording] MediaRecorder error:", e);
    };

    recorder.start(1000);
    recorderRef.current = recorder;
    const startedAt = Date.now();
    recordingStartTimeRef.current = startedAt;
    setRecording(true);
    setRecordingStartedAt(startedAt);

    const vCount = recordingStream.getVideoTracks().length;
    const aCount = recordingStream.getAudioTracks().length;
    const method = compositor ? "composited" : "single-track";
    // eslint-disable-next-line no-console
    console.info(
      `[recording] Started (${method}): ${vCount} video, ${aCount} audio, codec=${videoMimeType}`
    );
    return true;
  }, []);

  const stopRecording = useCallback((): Promise<RecordingResult | null> => {
    return new Promise((resolve) => {
      const recorder = recorderRef.current;
      if (!recorder || recorder.state === "inactive") {
        recorderRef.current = null;
        setRecording(false);
        setRecordingStartedAt(null);
        resolve(null);
        return;
      }

      recorder.onstop = async () => {
        if (compositorCleanupRef.current) {
          compositorCleanupRef.current();
          compositorCleanupRef.current = null;
        }

        audioSourcesRef.current.forEach((s) => {
          try { s.disconnect(); } catch { /* */ }
        });
        audioSourcesRef.current = [];
        mixedAudioTrackIdsRef.current = [];
        if (audioCtxRef.current && audioCtxRef.current.state !== "closed") {
          audioCtxRef.current.close().catch(() => {});
        }
        audioCtxRef.current = null;
        recorderRef.current = null;

        setRecording(false);
        setRecordingStartedAt(null);

        // Stop all per-user recorders (one per unique userId across the
        // whole call — they were kept alive through reconnects/leaves).
        // Done before the too-short-to-upload check so we never leak live
        // MediaRecorder + AudioContext instances if the user mis-clicks.
        const perUserSlots = Array.from(perUserRecordersRef.current.values());
        perUserRecordersRef.current = new Map();
        // Flush each per-user recorder's final chunk into the uploader.
        await Promise.all(perUserSlots.map(stopPerUserRecorder));
        if (perUserAudioCtxRef.current && perUserAudioCtxRef.current.state !== "closed") {
          perUserAudioCtxRef.current.close().catch(() => {});
        }
        perUserAudioCtxRef.current = null;

        const uploader = uploaderRef.current;
        uploaderRef.current = null;
        const elapsed = Date.now() - recordingStartTimeRef.current;
        // eslint-disable-next-line no-console
        console.info(`[recording] Stopped after ${elapsed}ms`);

        // Too short (mis-click) — discard the server-side session instead of
        // sealing a 1-frame recording.
        if (!uploader || elapsed < MIN_RECORDING_DURATION_MS) {
          // eslint-disable-next-line no-console
          console.warn(`[recording] Skipped: duration=${elapsed}ms`);
          if (uploader) uploader.abort();
          resolve(null);
          return;
        }

        setUploading(true);
        setUploadError(null);
        setUploadProgress({ loaded: 0, total: 0, phase: "uploading" });
        try {
          // finalize() seals the streaming session (server already holds the
          // bytes), or runs the legacy single POST if streaming wasn't active.
          const result = await uploader.finalize(
            Math.round(elapsed / 1000),
            (p) => setUploadProgress(p),
          );
          resolve(result);
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error("[recording] Upload failed:", err);
          const message = err instanceof Error ? err.message : "Upload failed";
          setUploadError(message);
          resolve(null);
        } finally {
          setUploading(false);
          setUploadProgress(null);
        }
      };

      if (recorder.state === "recording") {
        try { recorder.requestData(); } catch { /* */ }
      }
      recorder.stop();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Update streams, layout focus, and audio mixing during recording */
  const updateStreams = useCallback((streams: ProduceStream[], selectedStreamId?: string | null) => {
    if (!recorderRef.current) return;

    compositorStateRef.current = {
      ...compositorStateRef.current,
      streams,
      ...(selectedStreamId !== undefined ? { selectedStreamId } : {}),
    };

    // Update video elements for compositor
    if (videoElementsRef.current.size > 0) {
      const currentIds = new Set(videoElementsRef.current.keys());
      const newIds = new Set(streams.map((s) => s.id));

      currentIds.forEach((id) => {
        if (!newIds.has(id)) {
          const video = videoElementsRef.current.get(id);
          if (video) {
            try { video.pause(); video.srcObject = null; } catch { /* */ }
          }
          videoElementsRef.current.delete(id);
        }
      });

      streams.forEach((s) => {
        if (!currentIds.has(s.id) && s.stream) {
          const video = document.createElement("video");
          video.srcObject = s.stream;
          video.muted = true;
          video.playsInline = true;
          video.play().catch(() => {});
          videoElementsRef.current.set(s.id, video);
        }
      });
    }

    // Keep avatar map in lockstep with the active stream set so newly-
    // joined / departed users get their avatar tile drawn / dropped at
    // the next frame.
    const newIds = new Set(streams.map((s) => s.id));
    Array.from(avatarImagesRef.current.keys()).forEach((id) => {
      if (!newIds.has(id)) avatarImagesRef.current.delete(id);
    });
    streams.forEach((s) => {
      if (avatarImagesRef.current.has(s.id)) return;
      const img = loadAvatarImage(s);
      if (img) avatarImagesRef.current.set(s.id, img);
    });

    // Update audio mixing. Only disconnect/reconnect when the set of audio
    // tracks actually changed — otherwise active-speaker isSpeaking flips
    // (which arrive every 500 ms during normal conversation and rebuild
    // the `streams` array reference) would cause repeated WebAudio
    // disconnect/reconnect cycles and audible micro-glitches in the
    // recording.
    if (audioCtxRef.current && audioDestRef.current) {
      const desiredTracks: MediaStreamTrack[] = [];
      streams.forEach((s) => {
        if (!s.stream) return;
        if (s.isScreen) return; // skip screen-share entry; mic audio is in the camera entry
        const audioSource = s.audioStream || s.stream;
        audioSource.getAudioTracks().forEach((t) => desiredTracks.push(t));
      });
      const desiredIds = desiredTracks.map((t) => t.id).sort().join("|");
      const currentIds = (mixedAudioTrackIdsRef.current || []).slice().sort().join("|");
      if (desiredIds !== currentIds) {
        audioSourcesRef.current.forEach((s) => {
          try { s.disconnect(); } catch { /* */ }
        });
        const newSources: MediaStreamAudioSourceNode[] = [];
        desiredTracks.forEach((track) => {
          try {
            const source = audioCtxRef.current!.createMediaStreamSource(new MediaStream([track]));
            source.connect(audioDestRef.current!);
            newSources.push(source);
          } catch {
            /* */
          }
        });
        audioSourcesRef.current = newSources;
        mixedAudioTrackIdsRef.current = desiredTracks.map((t) => t.id);
      }
    }

    // Per-user recorders: start a new slot on first appearance, swap the
    // source on mute/unmute/mic-change. Slots are never stopped mid-call —
    // see the "Participant left" block below for why.
    const perUserBitrate =
      (navigator.hardwareConcurrency || 4) < 4
        ? PER_USER_AUDIO_BITRATE_LOW
        : PER_USER_AUDIO_BITRATE_DEFAULT;
    const liveUserIds = new Set<string>();
    const perUserCtx = perUserAudioCtxRef.current;
    const uploader = uploaderRef.current;
    streams.forEach((s) => {
      if (!s.stream) return;
      if (s.isScreen) return; // skip screen-share entry; mic audio is handled via the camera entry
      // Same id derivation as the initial recorder + ConferenceView metadata
      // (account id over the ephemeral session id) so late-joiners join too.
      const rawUserId = s.user ? recordingParticipantId(s.user) : "";
      const userId = rawUserId ? sanitizeUserId(rawUserId) : "";
      if (!userId) return;
      liveUserIds.add(userId);

      const audioSource = s.audioStream || s.stream;
      // Per-user recorder must read the mic — skip screen-share audio
      // (marked with kindType=screen in useProduceController.onScreenShare)
      // so transcription doesn't latch onto a silent shared-tab track.
      const liveTrack =
        getMicAudioTracks(audioSource).find((t) => t.readyState === "live") || null;

      const existing = perUserRecordersRef.current.get(userId);
      if (existing) {
        // Rejoined / unmuted — they're active again, so cancel any pending GC.
        existing.detachedAt = undefined;
        // Mute/unmute or mic switch: swap the source feeding the recorder
        // without stopping it. During mute the destination emits silence —
        // VAD in whisper-svc will skip it, so the transcript stays clean.
        if (perUserCtx) setPerUserRecorderSource(existing, liveTrack, perUserCtx);
        return;
      }
      // No slot yet — create one as soon as we see a live track.
      if (!liveTrack || !perUserCtx || !uploader) return;
      if (perUserRecordersRef.current.size >= MAX_PER_USER_RECORDERS) {
        // Make the cap a rolling window of *active* speakers: reclaim a slot
        // from someone who left more than the grace window ago. Their audio is
        // already streamed/buffered in the uploader (keyed by userId), so
        // stopping their client-side recorder loses nothing.
        const now = Date.now();
        let gcUid: string | null = null;
        let gcAt = Infinity;
        perUserRecordersRef.current.forEach((slot, uid) => {
          if (slot.detachedAt != null && now - slot.detachedAt > PER_USER_SLOT_GC_GRACE_MS && slot.detachedAt < gcAt) {
            gcAt = slot.detachedAt;
            gcUid = uid;
          }
        });
        if (gcUid != null) {
          const victim = perUserRecordersRef.current.get(gcUid);
          if (victim) void stopPerUserRecorder(victim);
          perUserRecordersRef.current.delete(gcUid);
        } else {
          if (!perUserCapWarnedRef.current.has(userId)) {
            perUserCapWarnedRef.current.add(userId);
            // eslint-disable-next-line no-console
            console.warn(
              `[recording] per-user transcription cap (${MAX_PER_USER_RECORDERS}) reached — ${userId} will NOT be transcribed`,
            );
          }
          return;
        }
      }
      // Late joiner: their track's t=0 is *now*, not recording start. Capture
      // the offset so the server can realign their transcript segments onto
      // the global timeline (see PerUserRecorderSlot.offsetMs).
      const offsetMs = Math.max(0, Date.now() - recordingStartTimeRef.current);
      const slot = startPerUserRecorder(userId, liveTrack, perUserBitrate, perUserCtx, offsetMs, uploader);
      if (slot) perUserRecordersRef.current.set(userId, slot);
    });

    // Participant left the call: don't stop their recorder — just detach
    // their source so silence is captured until either stopRecording() or
    // they rejoin (in which case setPerUserRecorderSource swaps the new
    // track in without losing the prior speech). This preserves audio
    // across WebRTC reconnects, tab refreshes, and leave-and-rejoin flows
    // where the same userId reappears with a fresh MediaStreamTrack.
    perUserRecordersRef.current.forEach((slot, userId) => {
      if (liveUserIds.has(userId)) return;
      if (perUserCtx) setPerUserRecorderSource(slot, null, perUserCtx);
      // Mark when they went silent so the GC above can reclaim the slot after
      // the grace window if a new speaker needs it.
      if (slot.detachedAt == null) slot.detachedAt = Date.now();
    });
  }, []);

  // On unmount while still recording (e.g. the owner is dropped from the call
  // and the conference UI tears down without a clean stop()), free local
  // capture resources and stop the heartbeat. We deliberately DON'T abort the
  // server session — its sweep auto-finalizes the bytes already streamed, so
  // the recording up to this point is preserved instead of lost.
  useEffect(() => {
    return () => {
      if (!recorderRef.current && !uploaderRef.current) return;
      try {
        const rec = recorderRef.current;
        if (rec && rec.state !== "inactive") rec.stop();
      } catch { /* */ }
      recorderRef.current = null;
      perUserRecordersRef.current.forEach((slot) => { try { slot.recorder.stop(); } catch { /* */ } });
      perUserRecordersRef.current = new Map();
      if (drawIntervalRef.current) { clearInterval(drawIntervalRef.current); drawIntervalRef.current = 0; }
      if (compositorCleanupRef.current) { try { compositorCleanupRef.current(); } catch { /* */ } compositorCleanupRef.current = null; }
      if (audioCtxRef.current && audioCtxRef.current.state !== "closed") audioCtxRef.current.close().catch(() => { /* */ });
      audioCtxRef.current = null;
      if (perUserAudioCtxRef.current && perUserAudioCtxRef.current.state !== "closed") perUserAudioCtxRef.current.close().catch(() => { /* */ });
      perUserAudioCtxRef.current = null;
      uploaderRef.current?.detach();
      uploaderRef.current = null;
    };
  }, []);

  return { recording, recordingStartedAt, uploading, uploadProgress, uploadError, startRecording, stopRecording, updateStreams };
}
